import { GraphQL } from "@rpi/openapi-api";

export interface ValidationEntry {
  source: string;
  humanReadableString: string;
  fatalError: boolean;
}

export interface AssetValidation {
  errors: ValidationEntry[];
  autofixes: ValidationEntry[];
  informatives: ValidationEntry[];
}

export interface GroupedValidation {
  cover: AssetValidation;
  pages: AssetValidation;
}

export function groupValidationEntries(validationResult: unknown): GroupedValidation {
  const result: GroupedValidation = {
    cover: { errors: [], autofixes: [], informatives: [] },
    pages: { errors: [], autofixes: [], informatives: [] },
  };

  const entries = extractValidationEntries(validationResult);

  for (const entry of entries) {
    if (!isValidationEntry(entry)) continue;

    const assets = parseAssets(entry.source);
    const type = parseType(entry.source);
    if (assets.length === 0 || !type) continue;

    for (const asset of assets) {
      result[asset][type].push(entry);
    }
  }

  return result;
}

function extractValidationEntries(validationResult: unknown): unknown[] {
  if (Array.isArray(validationResult)) return validationResult;

  if (typeof validationResult === "object" && validationResult !== null) {
    const obj = validationResult as Record<string, unknown>;
    if (Array.isArray(obj.validations)) return obj.validations;
  }

  return [];
}

export function getVisibleErrors(assetValidation: AssetValidation, isSuperAdmin: boolean): ValidationEntry[] {
  if (isSuperAdmin) return assetValidation.errors;
  const hasAutofixes = assetValidation.autofixes.length > 0;
  return assetValidation.errors.filter((e) => e.fatalError || !hasAutofixes);
}

export function getVisibleAutofixes(assetValidation: AssetValidation, isSuperAdmin: boolean): ValidationEntry[] {
  if (!isSuperAdmin) return [];
  return assetValidation.autofixes;
}

export function getVisibleInformatives(assetValidation: AssetValidation, isSuperAdmin: boolean): ValidationEntry[] {
  if (!isSuperAdmin) return [];
  return assetValidation.informatives;
}

export function hasAnyValidationMessages(validation: GroupedValidation, isSuperAdmin: boolean): boolean {
  const hasMessages = (asset: AssetValidation): boolean =>
    getVisibleErrors(asset, isSuperAdmin).length > 0 ||
    getVisibleAutofixes(asset, isSuperAdmin).length > 0 ||
    getVisibleInformatives(asset, isSuperAdmin).length > 0;

  return hasMessages(validation.cover) || hasMessages(validation.pages);
}

export function formatItemTitle(id: number, productName?: string | null): string {
  return productName ? `${id} (${productName})` : `${id}`;
}

export function isAssetPurged(status?: GraphQL.PrintAssetStatus | null): boolean {
  return status === GraphQL.PrintAssetStatus.Purged;
}

export function getSkuThumbnailUrl(sku?: string | null): string | undefined {
  if (sku?.includes("SCIW")) return "/assets/images/book-covers/view_SC_product_details.png";
  if (sku?.includes("HCIW")) return "/assets/images/book-covers/view_HC_product_details.png";
  return undefined;
}

function isValidationEntry(value: unknown): value is ValidationEntry {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.source === "string" && typeof obj.humanReadableString === "string" && typeof obj.fatalError === "boolean"
  );
}

type AssetKey = "cover" | "pages";

function parseAssets(source: string): AssetKey[] {
  if (source.endsWith("-CoverAndPages")) return ["cover", "pages"];
  if (source.endsWith("-Cover")) return ["cover"];
  if (source.endsWith("-Pages")) return ["pages"];
  return [];
}

type ValidationType = "errors" | "autofixes" | "informatives";

function parseType(source: string): ValidationType | null {
  if (source.startsWith("PrintAssetError")) return "errors";
  if (source.startsWith("PrintAssetAutofix")) return "autofixes";
  if (source.startsWith("PrintAssetInformative")) return "informatives";
  return null;
}
